import { Address } from 'viem';
import { mainnet } from 'viem/chains';

import { VAULT_TRANCHE, VaultTrancheId } from '@/app/shared/constants/vaults';
import {
  ALLOWLIST_ADDRESS,
  HASTRA_JUNIOR_TRANCHE,
  HASTRA_MZUSD,
  HASTRA_SENIOR_TRANCHE,
  HASTRA_VAULT,
  JUNIOR_TRANCHE,
  MZUSD,
  SENIOR_TRANCHE,
  USDC,
  VAULT,
  WYLDS,
} from '@/app/shared/constants/web3/constants';
import { resolveTrancheId } from '@/app/shared/lib/tranche';

export type ChainContracts = {
  USDC: Address;
  ASSET: Address;
  MZUSD: Address;
  ALLOWLIST: Address;
  SENIOR_TRANCHE: Address;
  JUNIOR_TRANCHE: Address;
  VAULT?: Address;
};

type SharedContracts = {
  USDC: Address;
  ALLOWLIST: Address;
};

type VaultContracts = {
  VAULT: Address;
  MZUSD: Address;
  SENIOR_TRANCHE: Address;
  JUNIOR_TRANCHE: Address;
  ASSET: Address;
};

const MAINNET_SHARED: SharedContracts = {
  USDC,
  ALLOWLIST: ALLOWLIST_ADDRESS,
};

const MAINNET_VAULTS: Record<string, VaultContracts> = {
  [VAULT.toLowerCase()]: {
    VAULT,
    MZUSD,
    SENIOR_TRANCHE,
    JUNIOR_TRANCHE,
    ASSET: USDC,
  },
  [HASTRA_VAULT.toLowerCase()]: {
    VAULT: HASTRA_VAULT,
    MZUSD: HASTRA_MZUSD,
    SENIOR_TRANCHE: HASTRA_SENIOR_TRANCHE,
    JUNIOR_TRANCHE: HASTRA_JUNIOR_TRANCHE,
    ASSET: WYLDS,
  },
};

const normalizeAddress = (address?: string): string | undefined => address?.toLowerCase();

export const getSharedChainContracts = (chainId?: number): SharedContracts | undefined => {
  if (chainId === mainnet.id) return MAINNET_SHARED;
  return undefined;
};

export const getVaultContracts = (
  chainId?: number,
  vaultAddress?: string,
): ChainContracts | undefined => {
  if (!chainId || !vaultAddress) return undefined;
  if (chainId === mainnet.id) {
    const vault = MAINNET_VAULTS[normalizeAddress(vaultAddress) ?? ''];
    if (!vault) return undefined;
    return {
      ...MAINNET_SHARED,
      ...vault,
    };
  }
  return undefined;
};

export type ChainMzusdToken = {
  vaultAddress: Address;
  mzusd: Address;
};

export const getChainMzusdTokens = (chainId?: number): ChainMzusdToken[] => {
  if (chainId === mainnet.id) {
    return Object.values(MAINNET_VAULTS).map((vault) => ({
      vaultAddress: vault.VAULT,
      mzusd: vault.MZUSD,
    }));
  }
  return [];
};

export const vaultUsesWyldsAsset = (contracts?: ChainContracts): boolean =>
  !!contracts && contracts.ASSET.toLowerCase() === WYLDS.toLowerCase();

export const getTrancheAddressById = (
  contracts: ChainContracts,
  trancheId: VaultTrancheId,
): Address | undefined => {
  if (trancheId === VAULT_TRANCHE.SENIOR) return contracts.SENIOR_TRANCHE;
  if (trancheId === VAULT_TRANCHE.JUNIOR) return contracts.JUNIOR_TRANCHE;
  return undefined;
};

export const getTrancheAddressByLabel = (
  contracts: ChainContracts,
  label?: string,
): Address | undefined => {
  const trancheId = resolveTrancheId(label);
  return trancheId ? getTrancheAddressById(contracts, trancheId) : undefined;
};
